/** Single course row in the portfolio list — title, progress bar, and last-accessed timestamp. */
import { Link } from 'react-router-dom';
import { useCourseProgress } from '@/shared/api/hooks/useEnrolments';
import type { EnrolmentWithCourse } from '@/shared/api/hooks/useEnrolments';
import { formatRelativeTime } from '@/shared/utils/formatRelativeTime';
import { courseHue } from '@/shared/utils/courseHue';

interface CourseRowProps {
  enrolment: EnrolmentWithCourse;
}

/**
 * Single course row — the row background itself acts as a progress indicator.
 * A subtle colour wash fills from the left proportional to completion,
 * visually connecting the text on the left with the percentage on the right.
 *
 * If the course is unpublished, the row is rendered as a non-interactive div
 * with reduced opacity and a "Currently unavailable" label.
 */
export default function CourseRow({ enrolment }: CourseRowProps) {
  const { courses: course, completedAt, lastAccessedAt } = enrolment;
  const isUnavailable = !course.isPublished;
  const { data: progress } = useCourseProgress(isUnavailable ? undefined : course.id);

  const percentage = progress?.percentage ?? 0;
  const totalLessons = progress?.totalLessons ?? 0;
  const completedLessons = progress?.completedLessons ?? 0;
  const isCompleted = !!completedAt || percentage === 100;

  const hue = courseHue(course.id);

  // Build secondary info fragments
  const fragments: string[] = [];
  if (isUnavailable) {
    fragments.push('Currently unavailable');
  } else {
    if (totalLessons > 0) {
      fragments.push(
        isCompleted ? `${totalLessons} lessons` : `${completedLessons} of ${totalLessons} lessons`
      );
    }
    if (lastAccessedAt) {
      fragments.push(formatRelativeTime(lastAccessedAt));
    }
  }

  // Progress fill colour — course hue tint for active, success tint for completed
  const fillColour = isCompleted ? `hsla(${hue}, 45%, 55%, 0.06)` : `hsla(${hue}, 55%, 60%, 0.09)`;
  const fillPct = Math.max(percentage, percentage > 0 ? 4 : 0);

  const rowContent = (
    <>
      {/* Track — subtle neutral fill across entire row */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `hsla(${hue}, 20%, 60%, 0.04)`,
          borderRadius: 'inherit',
        }}
      />

      {/* Progress fill — stronger tint from left edge */}
      {!isUnavailable && percentage > 0 && (
        <div
          className="pointer-events-none absolute inset-y-0 left-0"
          style={{
            width: `${fillPct}%`,
            background: fillColour,
            transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      )}

      {/* Hover state — separate layer so it doesn't interfere with progress fill */}
      {!isUnavailable && (
        <div
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          style={{ background: 'var(--color-surface-muted)' }}
        />
      )}

      {/* Activity indicator — coloured dot for active courses */}
      {!isUnavailable && !isCompleted && percentage > 0 && (
        <div
          className="relative shrink-0 rounded-full"
          style={{
            width: 7,
            height: 7,
            background: `hsl(${hue}, 55%, 55%)`,
            boxShadow: `0 0 6px hsla(${hue}, 55%, 55%, 0.3)`,
          }}
        />
      )}

      {/* Info column — title + secondary line */}
      <div className="relative min-w-0 flex-1">
        <span
          className="block truncate font-medium"
          style={{
            fontSize: 'var(--text-sm)',
            color: isUnavailable || isCompleted ? 'var(--color-text-muted)' : 'var(--color-text)',
          }}
        >
          {course.title}
        </span>

        {fragments.length > 0 && (
          <span
            className="mt-0.5 block truncate"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}
          >
            {!isUnavailable && isCompleted && 'Completed · '}
            {fragments.join(' · ')}
          </span>
        )}
      </div>

      {/* Percentage — right side, hue-coloured for active, muted for completed */}
      {!isUnavailable && (
        <span
          className="relative shrink-0 tabular-nums"
          style={{
            fontSize: 'var(--text-xs)',
            color: isCompleted ? 'var(--color-text-muted)' : `hsl(${hue}, 45%, 48%)`,
            fontWeight: isCompleted ? 400 : 500,
            minWidth: 28,
            textAlign: 'right',
          }}
        >
          {isCompleted ? '✓' : `${percentage}%`}
        </span>
      )}
    </>
  );

  if (isUnavailable) {
    return (
      <div
        className="relative -mx-3 flex items-center gap-3 overflow-hidden rounded-xl px-3 py-3.5"
        style={{ color: 'var(--color-text)', opacity: 0.55, cursor: 'default' }}
      >
        {rowContent}
      </div>
    );
  }

  return (
    <Link
      to={`/courses/${course.id}`}
      className="group relative -mx-3 flex items-center gap-3 overflow-hidden rounded-xl px-3 py-3.5 transition-colors duration-150"
      style={{ color: 'var(--color-text)', textDecoration: 'none' }}
    >
      {rowContent}
    </Link>
  );
}
